import { AxiosError } from 'axios';
import axiosInstance from '../utils/axios';

/**
 * Adaptive practice (monetization feature #03).
 *
 * Gated on the `subscription` SKU, so — like the paid review — a `402` here is a product
 * state and not a failure. It gets its own result variant rather than collapsing into an
 * error, because the screen it drives is an upsell, not an apology.
 *
 * A practice set carries no answer key: it is fetched before the student answers, so
 * anything in it would be readable up front. Grading is a second call, after the answer
 * is committed, and that is where the explanation comes from.
 */

export interface PracticeItem {
  itemId: string;
  question: string;
  options: string[];
  cognitiveSkill: string | null;
  difficulty: number | null;
  /** "synthetic" while difficulty is seeded rather than calibrated. */
  difficultySource: string | null;
}

export interface SkillMastery {
  cognitiveSkill: string;
  attempted: number;
  correct: number;
  /** 0–100. Null when never attempted — unknown is not the same as zero. */
  masteryPct: number | null;
}

export interface PracticeSet {
  studentId: string;
  requestedSize: number;
  returnedSize: number;
  /** Skills this set is aimed at, weakest first. */
  targetedSkills: string[];
  items: PracticeItem[];
  mastery: SkillMastery[];
  /** Set when fewer items came back than asked for, saying why. */
  note: string | null;
}

export interface GradedAnswer {
  itemId: string;
  /** Null when the item has no usable answer key — "ungraded", not "wrong". */
  correct: boolean | null;
  correctAnswerIndex: number | null;
  correctAnswer: string | null;
  submittedAnswerIndex: number | null;
  explanation: string | null;
  hints: string[] | null;
  cognitiveSkill: string | null;
  skillProgress: SkillMastery | null;
  note: string | null;
}

export type PracticeSetResult =
  | { state: 'ok'; set: PracticeSet }
  | { state: 'locked' }
  | { state: 'unauthorized' }
  | { state: 'error'; message: string };

export type GradeResult =
  | { state: 'ok'; graded: GradedAnswer }
  | { state: 'locked' }
  | { state: 'error'; message: string };

export const fetchPracticeSet = async (
  studentId: string,
  size?: number
): Promise<PracticeSetResult> => {
  try {
    const response = await axiosInstance.post(
      `/students/${studentId}/practice/next-set`,
      null,
      { params: size ? { size } : undefined }
    );
    if (response.data?.status === 'success' && response.data?.data) {
      return { state: 'ok', set: response.data.data as PracticeSet };
    }
    return { state: 'error', message: response.data?.message ?? 'Unexpected response' };
  } catch (err) {
    const status = (err as AxiosError)?.response?.status;
    // 402 is the entitlement gate in learning-service, passed through by the BFF.
    if (status === 402) return { state: 'locked' };
    if (status === 401 || status === 403) return { state: 'unauthorized' };
    return { state: 'error', message: 'Could not load practice questions. Please try again.' };
  }
};

/** A null `answerIndex` records the question as skipped. */
export const gradePracticeAnswer = async (
  studentId: string,
  itemId: string,
  answerIndex: number | null,
  timeTakenSeconds?: number
): Promise<GradeResult> => {
  try {
    const response = await axiosInstance.post(`/students/${studentId}/practice/answer`, {
      itemId,
      answerIndex,
      timeTakenSeconds
    });
    if (response.data?.status === 'success' && response.data?.data) {
      return { state: 'ok', graded: response.data.data as GradedAnswer };
    }
    return { state: 'error', message: response.data?.message ?? 'Unexpected response' };
  } catch (err) {
    const status = (err as AxiosError)?.response?.status;
    if (status === 402) return { state: 'locked' };
    return { state: 'error', message: 'Could not record that answer. Please try again.' };
  }
};
